import Head from "next/head";
import React, { useContext, useState } from "react";
import Footer from "../../components/Footer";
import Navigation from "../../components/Navigation";
import WordpressApi, { Menus } from "../../services/wordpressApi";
import ComposerBlocks from "../../components/ComposerBlocks";
import Seo from "../../components/Seo/Seo";
import { Container, Row, Col } from "react-bootstrap";
import { BlogCard } from "../../components/elements/Card/Card";
import LanguageContext, {
  LanguageContextProvider,
} from "../../components/LanguageContext";
import ModalFormsChecker from "../../components/ModalFormsChecker/ModalFormsChecker";
import { reducePostsArrayToPages } from "../../utils/blogUtils";
import styles from "./blogStyles.module.css";
import Button from "../../components/elements/Button";
import { translations } from "../../components/elements/translations";

export interface Post {
  slug: string;
  title: string;
  excerpt: string;
  image: { url: string };
  date: string;
  author: {
    name: string;
  };
  category: {
    name: string;
    id: number;
  };
}

export interface Blog {
  posts: Post[];
  data: any[];
  menus: Menus;
  tags: any;
  htmlHead: string;
  lang: string;
}

export default function Blog({
  posts,
  data,
  menus,
  tags,
  htmlHead,
  lang = "de",
}: Blog) {
  const pages = reducePostsArrayToPages(posts, 12);
  const [pageIndex, setPageIndex] = useState(0);
  const { language } = useContext(LanguageContext);
  const headerBlock: any[] = data.slice(0, 1);
  const blocks = data.slice(1);

  const shouldRenderNextPageButton = () => {
    return pageIndex < pages.length - 1;
  };

  const shouldRenderPreviousPageButton = () => {
    return pageIndex > 0;
  };

  return (
    <div>
      <LanguageContextProvider lang={lang}>
        <Seo header={htmlHead} />
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, minimal-scale=1.0"
          />
        </Head>
        <Navigation menu={menus.primary} />
        <main>
          {headerBlock && <ComposerBlocks data={headerBlock} />}
          <div className={styles.blogWrapper}>
            <Container>
              <Row className="mb-5"></Row>
              <Row>
                {pages[pageIndex].map((element, index) => (
                  <Col
                    key={JSON.stringify(element) + index}
                    xs={12}
                    md={4}
                    className={styles.blogPostWrapper}
                  >
                    <BlogCard
                      blogStyle={true}
                      date={element.date}
                      header={element.title}
                      text={element.excerpt}
                      image={
                        element.image && element.image?.url
                          ? element.image.url
                          : ""
                      }
                      blogPostUrl={element.slug}
                      category={element.category.name}
                    />
                  </Col>
                ))}
              </Row>

              {pages.length > 1 && (
                <Row className={styles.pageButtonWrapper}>
                  <Col className={styles.pageButtonWrapperColumn}>
                    {shouldRenderPreviousPageButton() && (
                      <Col className={styles.pageButtonWrapperColumn}>
                        <Button
                          variant={"primary"}
                          onClick={() => setPageIndex(pageIndex - 1)}
                        >
                          {translations[language].blogPageNewerPosts}
                        </Button>
                      </Col>
                    )}
                    {shouldRenderNextPageButton() && (
                      <Button
                        variant={"primary"}
                        onClick={() => setPageIndex(pageIndex + 1)}
                      >
                        {" "}
                        {translations[language].blogPageOlderPosts}{" "}
                      </Button>
                    )}
                  </Col>
                </Row>
              )}
              {/* <Pagination /> */}
            </Container>
          </div>
          {blocks && <ComposerBlocks data={blocks} />}
          <ModalFormsChecker />
        </main>
        <Footer {...menus} />
      </LanguageContextProvider>
    </div>
  );
}

export async function getServerSideProps({ query }: any) {
  let data: any;
  let page: any;
  let menus = {};
  let pageNumber = query.page;

  try {
    [data, page, menus] = await Promise.all([
      WordpressApi.getBlogPosts(pageNumber),
      WordpressApi.getPage("news-blog-deutsch-sprachschule"),
      WordpressApi.getMenus(),
    ]);
  } catch (error) {
    console.log(error);
    return {
      notFound: true,
    };
  }

  return {
    props: {
      posts: data,
      tags: page.tags,
      htmlHead: page?.htmlHead,
      data: page.blocks,
      menus: menus,
    },
  };
}
